// SPDX-License-Identifier: AGPL-3.0-or-later

@file:OptIn(ExperimentalTime::class)

package kairos

import kairos.Modification.Keep
import kairos.Modification.Update
import kotlin.jvm.JvmInline
import kotlin.reflect.KClass
import kotlin.time.ExperimentalTime
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.serialization.InternalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SealedClassSerializer
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.descriptors.element
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonEncoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.double
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import one.wabbit.parsing.charset.CharSet

@Serializable
sealed interface Modification<out R> {
    @Serializable data class Update<out R>(val value: R) : Modification<R>

    @Serializable data object Keep : Modification<Nothing>
}

operator fun <R> Modification<R>.invoke(oldValue: R): R =
    when (this) {
        is Update -> this.value
        is Keep -> oldValue
    }

@OptIn(InternalSerializationApi::class)
class BoolExprSerializer<Prim>(private val primitiveSerializer: KSerializer<Prim>) :
    KSerializer<BoolExpr<Prim>> {
    @Suppress("UNCHECKED_CAST")
    private val delegate: KSerializer<BoolExpr<Prim>> by lazy {
        SealedClassSerializer(
            serialName = "kairos.BoolExpr",
            baseClass = BoolExpr::class as KClass<BoolExpr<Prim>>,
            subclasses =
                arrayOf(
                    BoolExpr.And::class as KClass<out BoolExpr<Prim>>,
                    BoolExpr.Not::class as KClass<out BoolExpr<Prim>>,
                    BoolExpr.Or::class as KClass<out BoolExpr<Prim>>,
                    BoolExpr.Primitive::class as KClass<out BoolExpr<Prim>>,
                ),
            subclassSerializers =
                arrayOf(
                    BoolExprAndSerializer(this),
                    BoolExprNotSerializer(this),
                    BoolExprOrSerializer(this),
                    BoolExprPrimitiveSerializer(primitiveSerializer),
                ),
        )
    }

    override val descriptor: SerialDescriptor
        get() = delegate.descriptor

    override fun serialize(encoder: Encoder, value: BoolExpr<Prim>) {
        delegate.serialize(encoder, value)
    }

    override fun deserialize(decoder: Decoder): BoolExpr<Prim> = delegate.deserialize(decoder)
}

object KpiTargetSerializer : KSerializer<Kairos.KpiTarget> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.KpiTarget") {
            element<String>("type")
            element<Double>("value", isOptional = true)
            element<Double>("min", isOptional = true)
            element<Double>("max", isOptional = true)
        }

    override fun serialize(encoder: Encoder, value: Kairos.KpiTarget) {
        val jsonEncoder =
            encoder as? JsonEncoder
                ?: throw SerializationException("KpiTargetSerializer only supports JSON")
        val json =
            when (value) {
                is Kairos.KpiTarget.AtLeast ->
                    buildJsonObject {
                        put("type", JsonPrimitive("at_least"))
                        put("value", JsonPrimitive(value.value))
                    }

                is Kairos.KpiTarget.AtMost ->
                    buildJsonObject {
                        put("type", JsonPrimitive("at_most"))
                        put("value", JsonPrimitive(value.value))
                    }

                is Kairos.KpiTarget.Range ->
                    buildJsonObject {
                        put("type", JsonPrimitive("range"))
                        put("min", JsonPrimitive(value.min))
                        put("max", JsonPrimitive(value.max))
                    }
            }
        jsonEncoder.encodeJsonElement(json)
    }

    override fun deserialize(decoder: Decoder): Kairos.KpiTarget {
        val jsonDecoder =
            decoder as? JsonDecoder
                ?: throw SerializationException("KpiTargetSerializer only supports JSON")
        val jsonObject = jsonDecoder.decodeJsonElement().jsonObject
        val type = jsonObject.requiredString("type")
        return when (type) {
            "at_least",
            "AtLeast",
            "kairos.KpiTarget.AtLeast",
            "kairos.Kairos.KpiTarget.AtLeast" -> {
                Kairos.KpiTarget.AtLeast(jsonObject.requiredDouble("value"))
            }

            "at_most",
            "AtMost",
            "kairos.KpiTarget.AtMost",
            "kairos.Kairos.KpiTarget.AtMost" -> {
                Kairos.KpiTarget.AtMost(jsonObject.requiredDouble("value"))
            }

            "range",
            "Range",
            "kairos.KpiTarget.Range",
            "kairos.Kairos.KpiTarget.Range" -> {
                Kairos.KpiTarget.Range(
                    min = jsonObject.requiredDouble("min"),
                    max = jsonObject.requiredDouble("max"),
                )
            }

            else -> throw SerializationException("Unknown KPI target type '$type'")
        }
    }
}

object KpiFrequencySerializer : KSerializer<Kairos.KpiFrequency> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.KpiFrequency") {
            element<String>("type")
            element<TimeSpan>("span", isOptional = true)
        }

    override fun serialize(encoder: Encoder, value: Kairos.KpiFrequency) {
        val jsonEncoder =
            encoder as? JsonEncoder
                ?: throw SerializationException("KpiFrequencySerializer only supports JSON")
        val json =
            when (value) {
                Kairos.KpiFrequency.Daily -> kpiTypeJson("daily")
                Kairos.KpiFrequency.Weekly -> kpiTypeJson("weekly")
                Kairos.KpiFrequency.Monthly -> kpiTypeJson("monthly")
                Kairos.KpiFrequency.Quarterly -> kpiTypeJson("quarterly")
                Kairos.KpiFrequency.Yearly -> kpiTypeJson("yearly")
                is Kairos.KpiFrequency.Custom ->
                    buildJsonObject {
                        put("type", JsonPrimitive("custom"))
                        put(
                            "span",
                            jsonEncoder.json.encodeToJsonElement(TimeSpan.serializer(), value.span),
                        )
                    }
            }
        jsonEncoder.encodeJsonElement(json)
    }

    override fun deserialize(decoder: Decoder): Kairos.KpiFrequency {
        val jsonDecoder =
            decoder as? JsonDecoder
                ?: throw SerializationException("KpiFrequencySerializer only supports JSON")
        val jsonObject = jsonDecoder.decodeJsonElement().jsonObject
        return when (jsonObject.requiredString("type").normalizedKpiType()) {
            "daily" -> Kairos.KpiFrequency.Daily
            "weekly" -> Kairos.KpiFrequency.Weekly
            "monthly" -> Kairos.KpiFrequency.Monthly
            "quarterly" -> Kairos.KpiFrequency.Quarterly
            "yearly" -> Kairos.KpiFrequency.Yearly
            "custom" ->
                Kairos.KpiFrequency.Custom(
                    jsonObject.requiredElement("span").let { element ->
                        jsonDecoder.json.decodeFromJsonElement(TimeSpan.serializer(), element)
                    }
                )
            else ->
                throw SerializationException(
                    "Unknown KPI frequency type '${jsonObject.requiredString("type")}'"
                )
        }
    }
}

object KpiUnitSerializer : KSerializer<Kairos.KpiUnit> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.KpiUnit") { element<String>("type") }

    override fun serialize(encoder: Encoder, value: Kairos.KpiUnit) {
        val jsonEncoder =
            encoder as? JsonEncoder
                ?: throw SerializationException("KpiUnitSerializer only supports JSON")
        val type =
            when (value) {
                Kairos.KpiUnit.Weight -> "weight"
                Kairos.KpiUnit.Length -> "length"
                Kairos.KpiUnit.Time -> "time"
                Kairos.KpiUnit.Currency -> "currency"
                Kairos.KpiUnit.Count -> "count"
                Kairos.KpiUnit.Percentage -> "percentage"
                Kairos.KpiUnit.Score -> "score"
            }
        jsonEncoder.encodeJsonElement(kpiTypeJson(type))
    }

    override fun deserialize(decoder: Decoder): Kairos.KpiUnit {
        val jsonDecoder =
            decoder as? JsonDecoder
                ?: throw SerializationException("KpiUnitSerializer only supports JSON")
        return when (
            jsonDecoder.decodeJsonElement().jsonObject.requiredString("type").normalizedKpiType()
        ) {
            "weight" -> Kairos.KpiUnit.Weight
            "length" -> Kairos.KpiUnit.Length
            "time" -> Kairos.KpiUnit.Time
            "currency" -> Kairos.KpiUnit.Currency
            "count" -> Kairos.KpiUnit.Count
            "percentage" -> Kairos.KpiUnit.Percentage
            "score" -> Kairos.KpiUnit.Score
            else -> throw SerializationException("Unknown KPI unit type")
        }
    }
}

object KpiOriginSerializer : KSerializer<Kairos.KpiOrigin> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.KpiOrigin") {
            element<String>("type")
            element<Kairos.KpiFrequency>("frequency", isOptional = true)
            element<Kairos.KpiUnit>("unit", isOptional = true)
            element<Kairos.KpiAccumulationType>("accumulationType", isOptional = true)
            element<Kairos.KpiExpr>("formula", isOptional = true)
        }

    override fun serialize(encoder: Encoder, value: Kairos.KpiOrigin) {
        val jsonEncoder =
            encoder as? JsonEncoder
                ?: throw SerializationException("KpiOriginSerializer only supports JSON")
        val json =
            when (value) {
                is Kairos.KpiOrigin.Measured ->
                    buildJsonObject {
                        put("type", JsonPrimitive("measured"))
                        put(
                            "frequency",
                            jsonEncoder.json.encodeToJsonElement(
                                KpiFrequencySerializer,
                                value.frequency,
                            ),
                        )
                        put(
                            "unit",
                            jsonEncoder.json.encodeToJsonElement(KpiUnitSerializer, value.unit),
                        )
                        put(
                            "accumulationType",
                            jsonEncoder.json.encodeToJsonElement(
                                Kairos.KpiAccumulationType.serializer(),
                                value.accumulationType,
                            ),
                        )
                    }
                is Kairos.KpiOrigin.Derived ->
                    buildJsonObject {
                        put("type", JsonPrimitive("derived"))
                        put(
                            "formula",
                            jsonEncoder.json.encodeToJsonElement(
                                Kairos.KpiExpr.serializer(),
                                value.formula,
                            ),
                        )
                    }
            }
        jsonEncoder.encodeJsonElement(json)
    }

    override fun deserialize(decoder: Decoder): Kairos.KpiOrigin {
        val jsonDecoder =
            decoder as? JsonDecoder
                ?: throw SerializationException("KpiOriginSerializer only supports JSON")
        val jsonObject = jsonDecoder.decodeJsonElement().jsonObject
        return when (jsonObject.requiredString("type").normalizedKpiType()) {
            "measured" ->
                Kairos.KpiOrigin.Measured(
                    frequency =
                        jsonDecoder.json.decodeFromJsonElement(
                            KpiFrequencySerializer,
                            jsonObject.requiredElement("frequency"),
                        ),
                    unit =
                        jsonDecoder.json.decodeFromJsonElement(
                            KpiUnitSerializer,
                            jsonObject.requiredElement("unit"),
                        ),
                    accumulationType =
                        jsonDecoder.json.decodeFromJsonElement(
                            Kairos.KpiAccumulationType.serializer(),
                            jsonObject.requiredElement("accumulationType"),
                        ),
                )
            "derived" ->
                Kairos.KpiOrigin.Derived(
                    jsonDecoder.json.decodeFromJsonElement(
                        Kairos.KpiExpr.serializer(),
                        jsonObject.requiredElement("formula"),
                    )
                )
            else ->
                throw SerializationException(
                    "Unknown KPI origin type '${jsonObject.requiredString("type")}'"
                )
        }
    }
}

private fun JsonObject.requiredString(key: String): String =
    this[key]?.jsonPrimitive?.content
        ?: throw SerializationException("Missing '$key' in JSON object")

private fun JsonObject.requiredDouble(key: String): Double =
    this[key]?.jsonPrimitive?.double
        ?: throw SerializationException("Missing '$key' in JSON object")

private fun JsonObject.requiredElement(key: String): JsonElement =
    this[key] ?: throw SerializationException("Missing '$key' in JSON object")

private fun kpiTypeJson(type: String): JsonObject = buildJsonObject {
    put("type", JsonPrimitive(type))
}

private fun String.normalizedKpiType(): String = substringAfterLast('.').lowercase()

private fun resolveTimeZoneOrNull(timeZoneId: String): TimeZone? =
    try {
        TimeZone.of(timeZoneId)
    } catch (_: Throwable) {
        val current = TimeZone.currentSystemDefault()
        current.takeIf { timeZone -> timeZone.id == timeZoneId }
    }

private class BoolExprPrimitiveSerializer<Prim>(
    private val primitiveSerializer: KSerializer<Prim>
) : KSerializer<BoolExpr.Primitive<Prim>> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.BoolExpr.Primitive") {
            element("value", primitiveSerializer.descriptor)
        }

    override fun serialize(encoder: Encoder, value: BoolExpr.Primitive<Prim>) {
        val composite = encoder.beginStructure(descriptor)
        composite.encodeSerializableElement(descriptor, 0, primitiveSerializer, value.value)
        composite.endStructure(descriptor)
    }

    override fun deserialize(decoder: Decoder): BoolExpr.Primitive<Prim> {
        val composite = decoder.beginStructure(descriptor)
        var loaded = false
        var primitive: Prim? = null
        while (true) {
            when (val index = composite.decodeElementIndex(descriptor)) {
                CompositeDecoder.DECODE_DONE -> break
                0 -> {
                    primitive =
                        composite.decodeSerializableElement(descriptor, 0, primitiveSerializer)
                    loaded = true
                }
                else -> error("Unexpected BoolExpr.Primitive field index: $index")
            }
        }
        composite.endStructure(descriptor)
        require(loaded) { "BoolExpr.Primitive missing value" }
        @Suppress("UNCHECKED_CAST")
        return BoolExpr.Primitive(primitive as Prim)
    }
}

private class BoolExprAndSerializer<Prim>(
    private val boolExprSerializer: KSerializer<BoolExpr<Prim>>
) : KSerializer<BoolExpr.And<Prim>> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.BoolExpr.And") {
            element("exprs", buildClassSerialDescriptor("kairos.BoolExpr.And.exprs"))
        }

    override fun serialize(encoder: Encoder, value: BoolExpr.And<Prim>) {
        val composite = encoder.beginStructure(descriptor)
        composite.encodeSerializableElement(
            descriptor,
            0,
            ListSerializer(boolExprSerializer),
            value.exprs,
        )
        composite.endStructure(descriptor)
    }

    override fun deserialize(decoder: Decoder): BoolExpr.And<Prim> {
        val composite = decoder.beginStructure(descriptor)
        var loaded = false
        var exprs: List<BoolExpr<Prim>>? = null
        while (true) {
            when (val index = composite.decodeElementIndex(descriptor)) {
                CompositeDecoder.DECODE_DONE -> break
                0 -> {
                    exprs =
                        composite.decodeSerializableElement(
                            descriptor,
                            0,
                            ListSerializer(boolExprSerializer),
                        )
                    loaded = true
                }
                else -> error("Unexpected BoolExpr.And field index: $index")
            }
        }
        composite.endStructure(descriptor)
        require(loaded) { "BoolExpr.And missing exprs" }
        return BoolExpr.And(exprs.orEmpty())
    }
}

private class BoolExprOrSerializer<Prim>(
    private val boolExprSerializer: KSerializer<BoolExpr<Prim>>
) : KSerializer<BoolExpr.Or<Prim>> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.BoolExpr.Or") {
            element("exprs", buildClassSerialDescriptor("kairos.BoolExpr.Or.exprs"))
        }

    override fun serialize(encoder: Encoder, value: BoolExpr.Or<Prim>) {
        val composite = encoder.beginStructure(descriptor)
        composite.encodeSerializableElement(
            descriptor,
            0,
            ListSerializer(boolExprSerializer),
            value.exprs,
        )
        composite.endStructure(descriptor)
    }

    override fun deserialize(decoder: Decoder): BoolExpr.Or<Prim> {
        val composite = decoder.beginStructure(descriptor)
        var loaded = false
        var exprs: List<BoolExpr<Prim>>? = null
        while (true) {
            when (val index = composite.decodeElementIndex(descriptor)) {
                CompositeDecoder.DECODE_DONE -> break
                0 -> {
                    exprs =
                        composite.decodeSerializableElement(
                            descriptor,
                            0,
                            ListSerializer(boolExprSerializer),
                        )
                    loaded = true
                }
                else -> error("Unexpected BoolExpr.Or field index: $index")
            }
        }
        composite.endStructure(descriptor)
        require(loaded) { "BoolExpr.Or missing exprs" }
        return BoolExpr.Or(exprs.orEmpty())
    }
}

private class BoolExprNotSerializer<Prim>(
    private val boolExprSerializer: KSerializer<BoolExpr<Prim>>
) : KSerializer<BoolExpr.Not<Prim>> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.BoolExpr.Not") {
            element("expr", buildClassSerialDescriptor("kairos.BoolExpr.Not.expr"))
        }

    override fun serialize(encoder: Encoder, value: BoolExpr.Not<Prim>) {
        val composite = encoder.beginStructure(descriptor)
        composite.encodeSerializableElement(descriptor, 0, boolExprSerializer, value.expr)
        composite.endStructure(descriptor)
    }

    override fun deserialize(decoder: Decoder): BoolExpr.Not<Prim> {
        val composite = decoder.beginStructure(descriptor)
        var loaded = false
        var expr: BoolExpr<Prim>? = null
        while (true) {
            when (val index = composite.decodeElementIndex(descriptor)) {
                CompositeDecoder.DECODE_DONE -> break
                0 -> {
                    expr = composite.decodeSerializableElement(descriptor, 0, boolExprSerializer)
                    loaded = true
                }
                else -> error("Unexpected BoolExpr.Not field index: $index")
            }
        }
        composite.endStructure(descriptor)
        require(loaded) { "BoolExpr.Not missing expr" }
        return BoolExpr.Not(expr ?: error("BoolExpr.Not missing expr"))
    }
}

@Serializable(with = BoolExprSerializer::class)
sealed interface BoolExpr<out Prim> {
    @Serializable data class Primitive<out Prim>(val value: Prim) : BoolExpr<Prim>

    @Serializable data class And<out Prim>(val exprs: List<BoolExpr<Prim>>) : BoolExpr<Prim>

    @Serializable data class Or<out Prim>(val exprs: List<BoolExpr<Prim>>) : BoolExpr<Prim>

    @Serializable data class Not<out Prim>(val expr: BoolExpr<Prim>) : BoolExpr<Prim>

    companion object {
        val True = BoolExpr.And<Nothing>(emptyList())
        val False = BoolExpr.Or<Nothing>(emptyList())
    }
}

@Serializable
enum class TimeUnit {
    Seconds,
    Minutes,
    Hours,
    Days,
    Weeks,
    Months,
    Years,
}

object ZonedDateTimeSerializer : KSerializer<ZonedDateTime> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("kairos.ZonedDateTime") {
            element<LocalDateTime>("dateTime")
            element<String>("timeZone", isOptional = true)
        }

    override fun serialize(encoder: Encoder, value: ZonedDateTime) {
        val composite = encoder.beginStructure(descriptor)
        composite.encodeSerializableElement(
            descriptor,
            0,
            LocalDateTime.serializer(),
            value.dateTime,
        )
        value.timeZoneId?.let { timeZoneId ->
            composite.encodeStringElement(descriptor, 1, timeZoneId)
        }
        composite.endStructure(descriptor)
    }

    override fun deserialize(decoder: Decoder): ZonedDateTime {
        val composite = decoder.beginStructure(descriptor)
        var dateTime: LocalDateTime? = null
        var timeZoneId: String? = null
        while (true) {
            when (val index = composite.decodeElementIndex(descriptor)) {
                CompositeDecoder.DECODE_DONE -> break
                0 ->
                    dateTime =
                        composite.decodeSerializableElement(
                            descriptor,
                            0,
                            LocalDateTime.serializer(),
                        )
                1 -> timeZoneId = composite.decodeStringElement(descriptor, 1)
                else -> error("Unexpected ZonedDateTime field index: $index")
            }
        }
        composite.endStructure(descriptor)
        return ZonedDateTime(
            dateTime = dateTime ?: throw SerializationException("ZonedDateTime missing dateTime"),
            timeZoneId = timeZoneId,
        )
    }
}

@Serializable(with = ZonedDateTimeSerializer::class)
data class ZonedDateTime(val dateTime: LocalDateTime, val timeZoneId: String?) {
    constructor(dateTime: LocalDateTime, timeZone: TimeZone?) : this(dateTime, timeZone?.id)

    val timeZone: TimeZone?
        get() = timeZoneId?.let(::resolveTimeZoneOrNull)

    fun toInstant(): kotlin.time.Instant {
        val resolvedTimeZone = timeZone
        return if (resolvedTimeZone != null) {
            dateTime.toInstant(resolvedTimeZone)
        } else {
            dateTime.toInstant(TimeZone.UTC)
        }
    }

    override fun toString(): String = "$dateTime ${timeZoneId ?: "UTC"}"
}

@Serializable data class PrimitiveTimeSpan(val value: Long, val unit: TimeUnit)

@Serializable data class TimeSpan(val spans: List<PrimitiveTimeSpan>)

interface Kairos {
    @Serializable @JvmInline value class WorkspaceId(val value: String)

    @Serializable @JvmInline value class UserId(val value: String)

    @Serializable @JvmInline value class ProjectId(val value: String)

    @Serializable @JvmInline value class ObjectiveId(val value: String)

    @Serializable @JvmInline value class TicketId(val value: String)

    @Serializable @JvmInline value class TicketActivityId(val value: String)

    @Serializable @JvmInline value class KpiId(val value: String)

    @Serializable @JvmInline value class TimeboxId(val value: String)

    @Serializable @JvmInline value class EventId(val value: String)

    @Serializable @JvmInline value class RoutineId(val value: String)

    @Serializable @JvmInline value class KpiMeasurementId(val value: String)

    @Serializable
    data class Workspace(val id: WorkspaceId, val createdAt: Instant, val name: String) {
        init {
            require(name.isNotBlank()) { "Workspace name must not be blank" }
            require(CharSet.of(name).isSubsetOf(AllowedProjectChars)) {
                "Workspace name must be printable"
            }
        }
    }

    @Serializable
    sealed interface WorkspaceQ {
        @Serializable data class WithName(val name: String) : WorkspaceQ
    }

    @Serializable
    enum class WorkspaceMembershipRole {
        Owner,
        Member,
    }

    @Serializable
    data class WorkspaceMembership(
        val workspaceId: WorkspaceId,
        val userId: UserId,
        val createdAt: Instant,
        val role: WorkspaceMembershipRole = WorkspaceMembershipRole.Member,
    )

    @Serializable
    sealed interface WorkspaceMembershipQ {
        @Serializable data class WithWorkspace(val workspaceId: WorkspaceId) : WorkspaceMembershipQ

        @Serializable data class WithUser(val userId: UserId) : WorkspaceMembershipQ
    }

    @Serializable
    enum class UserRole {
        User,
        Admin,
    }

    @Serializable
    enum class UserKind {
        Human,
        Agent,
    }

    @Serializable
    data class User(
        val id: UserId,
        val createdAt: kotlin.time.Instant,
        val displayName: String,
        val email: String?,
        val avatarUrl: String? = null,
        val timeZoneId: String? = null,
        val localeTag: String? = null,
        val role: UserRole = UserRole.User,
        val kind: UserKind = UserKind.Human,
        val ownerUserId: UserId? = null,
    ) {
        init {
            require(displayName.isNotBlank()) { "User display name must not be blank" }
            require(CharSet.of(displayName).isSubsetOf(AllowedUserChars)) {
                "User display name must be printable"
            }
            require(avatarUrl?.isBlank() != true) { "User avatar URL must not be blank" }
            require(timeZoneId?.isBlank() != true) { "User time zone must not be blank" }
            require(localeTag?.isBlank() != true) { "User locale must not be blank" }
            require((kind == UserKind.Agent) == (ownerUserId != null)) {
                "Agent users must have an owner and human users must not"
            }
        }
    }

    @Serializable
    sealed interface UserQ {
        @Serializable data class WithDisplayName(val displayName: String) : UserQ

        @Serializable data class WithEmail(val email: String) : UserQ

        @Serializable data class WithRole(val role: UserRole) : UserQ

        @Serializable data class WithKind(val kind: UserKind) : UserQ

        @Serializable data class OwnedBy(val ownerUserId: UserId) : UserQ
    }

    @Serializable
    data class Project(
        val id: ProjectId,
        val createdAt: kotlin.time.Instant,
        val workspaceId: WorkspaceId,
        val name: String,
        val parentProjectId: ProjectId?,
    ) {
        init {
            require(name.isNotBlank()) { "Project name must not be blank" }
            require(CharSet.of(name).isSubsetOf(AllowedProjectChars)) {
                "Project name must be printable"
            }
            require(parentProjectId == null || parentProjectId != id) {
                "Project cannot be its own parent"
            }
        }
    }

    @Serializable
    sealed interface ProjectQ {
        @Serializable data class WithWorkspace(val workspaceId: WorkspaceId) : ProjectQ

        @Serializable data class WithName(val name: String) : ProjectQ

        @Serializable data class WithParent(val parentProjectId: ProjectId) : ProjectQ
    }

    @Serializable
    sealed interface ProjectField {
        @Serializable data object TimeSinceCreation : ProjectField
    }

    @Serializable
    enum class TicketPriority {
        Low,
        Medium,
        High,
    }

    @Serializable
    enum class TicketUrgency {
        LongTerm,
        Low,
        Medium,
        High,
    }

    @Serializable
    enum class TicketStage {
        Inbox,
        Todo,
        InProgress,
        Review,
        Blocked,
    }

    @Serializable
    data class Ticket(
        val id: TicketId,
        val createdAt: Instant,
        val projectId: ProjectId,
        val title: String,
        val stage: TicketStage = TicketStage.Todo,
        val description: String = "",
        val deadline: ZonedDateTime?,
        val priority: TicketPriority?,
        val urgency: TicketUrgency?,
        val completedAt: Instant?,
        val assigneeUserId: UserId? = null,
    ) {
        init {
            require(title.isNotBlank()) { "Ticket title must not be blank" }
            require(CharSet.of(title).isSubsetOf(AllowedTicketChars)) {
                "Ticket title must be printable"
            }
        }
    }

    @Serializable
    data class TicketBlocker(
        val blockedTicketId: TicketId,
        val blockingTicketId: TicketId,
        val createdAt: Instant,
    ) {
        init {
            require(blockedTicketId != blockingTicketId) { "A ticket cannot be blocked by itself" }
        }
    }

    @Serializable
    sealed interface TicketQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : TicketQ

        @Serializable data object IsOpen : TicketQ

        @Serializable data object IsClosed : TicketQ

        @Serializable data object IsOverdue : TicketQ

        @Serializable data class WithPriority(val priority: TicketPriority) : TicketQ

        @Serializable data class WithUrgency(val urgency: TicketUrgency) : TicketQ

        @Serializable data class WithStage(val stage: TicketStage) : TicketQ

        @Serializable data class AssignedTo(val userId: UserId) : TicketQ
    }

    @Serializable
    sealed interface TicketBlockerQ {
        @Serializable data class ForBlockedTicket(val ticketId: TicketId) : TicketBlockerQ

        @Serializable data class ForBlockingTicket(val ticketId: TicketId) : TicketBlockerQ

        @Serializable data class InvolvingTicket(val ticketId: TicketId) : TicketBlockerQ
    }

    @Serializable
    sealed interface TicketField {
        @Serializable data object TimeSinceCreation : TicketField

        @Serializable data object TimeUntilDeadline : TicketField
    }

    @Serializable
    enum class TicketActivityKind {
        Created,
        Updated,
        Completed,
        Reopened,
        Commented,
        CommentDeleted,
    }

    @Serializable
    data class TicketActivity(
        val id: TicketActivityId,
        val createdAt: Instant,
        val ticketId: TicketId,
        val actorUserId: UserId?,
        val kind: TicketActivityKind,
        val summary: String,
    ) {
        init {
            require(summary.isNotBlank()) { "Ticket activity summary must not be blank" }
        }
    }

    @Serializable
    sealed interface TicketActivityQ {
        @Serializable data class WithTicket(val ticketId: TicketId) : TicketActivityQ

        @Serializable data class ByActor(val actorUserId: UserId) : TicketActivityQ

        @Serializable data class WithKind(val kind: TicketActivityKind) : TicketActivityQ
    }

    // Can be used to track time spent on a project, a ticket, or a KPI.
    // Can be used for pomodoro tracking, timeboxing, or time tracking.
    @Serializable
    data class Timebox(
        val id: TimeboxId,
        val createdAt: Instant,
        val workspaceId: WorkspaceId,
        val projectId: ProjectId?,
        val tickets: List<TicketId>,
        val description: String,
        val startTime: Instant,
        val recordedDuration: TimeSpan?,
    ) {
        init {
            require(description.isNotBlank()) { "Timebox description must not be blank" }
            require(CharSet.of(description).isSubsetOf(AllowedTimeboxChars)) {
                "Timebox description must be printable"
            }
        }
    }

    @Serializable
    sealed interface TimeboxQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : TimeboxQ

        @Serializable data class WithProject(val projectId: ProjectId) : TimeboxQ

        @Serializable data class RelatedToTicket(val ticketId: TicketId) : TimeboxQ
    }

    @Serializable
    sealed interface TimeboxField {
        @Serializable data object TimeSinceStart : TimeboxField

        @Serializable data object TimeUntilEnd : TimeboxField
    }

    // Can be used for tracking daily habits, routines, or reviews.
    @Serializable
    data class Routine(
        val id: RoutineId,
        val createdAt: Instant,
        val workspaceId: WorkspaceId,
        val projectId: ProjectId?,
        val description: String,
    )

    @Serializable
    sealed interface RoutineQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : RoutineQ

        @Serializable data class WithProject(val projectId: ProjectId) : RoutineQ
    }

    @Serializable
    sealed interface RoutineField {
        @Serializable data object TimeSinceStart : RoutineField
    }

    @Serializable
    data class Event(
        val id: EventId,
        val createdAt: Instant,
        val workspaceId: WorkspaceId,
        val projectId: ProjectId?,
        val title: String,
        // ICS: UID – a globally unique identifier for the event.
        val uniqueId: String,
        // ICS: DTSTAMP – the date/time stamp of when the event was created.
        val dtStamp: ZonedDateTime,
        // ICS: DTSTART – the start date/time of the event.
        val dtStart: ZonedDateTime,
        // ICS: DTEND – the end date/time of the event. Optional if a duration is provided.
        val dtEnd: ZonedDateTime?,
        // ICS: SUMMARY – a short summary or title for the event.
        val summary: String,
        // ICS: DESCRIPTION – an optional description of the event.
        val description: String?,
        // ICS: LOCATION – an optional location for the event.
        val location: String?,
    )

    @Serializable
    sealed interface EventQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : EventQ

        @Serializable data class WithProject(val projectId: ProjectId) : EventQ

        @Serializable data class WithTitle(val title: String) : EventQ
    }

    @Serializable
    sealed interface EventField {
        @Serializable data object TimeUntilStart : EventField

        @Serializable data object TimeUntilEnd : EventField
    }

    @Serializable(with = KpiTargetSerializer::class)
    sealed interface KpiTarget {
        // This automatically implies that higher is better.
        @Serializable @SerialName("at_least") data class AtLeast(val value: Double) : KpiTarget

        // This automatically implies that lower is better.
        @Serializable @SerialName("at_most") data class AtMost(val value: Double) : KpiTarget

        // This implies that the value should be within a certain range.
        // If the value is lower than the range, higher is better.
        // If the value is higher than the range, lower is better.
        @Serializable
        @SerialName("range")
        data class Range(val min: Double, val max: Double) : KpiTarget {
            init {
                require(min <= max) { "min must be less than or equal to max" }
            }
        }
    }

    @Serializable(with = KpiFrequencySerializer::class)
    sealed interface KpiFrequency {
        @Serializable data object Daily : KpiFrequency

        @Serializable data object Weekly : KpiFrequency

        @Serializable data object Monthly : KpiFrequency

        @Serializable data object Quarterly : KpiFrequency

        @Serializable data object Yearly : KpiFrequency

        @Serializable data class Custom(val span: TimeSpan) : KpiFrequency
    }

    // Some KPIs are “instantaneous” (e.g. weigh yourself and get 70 kg on a given day),
    // while others are “summation over a period” (e.g. number of social events this week)
    // Instantaneous KPIs can use LOCF because carrying the last known value forward is
    // a common way of saying “Until we get new information, assume it hasn’t changed.”
    // Summation KPIs measure how much happened during an interval. The value resets each
    // interval—so you can’t assume “Today’s steps = Yesterday’s steps” if we didn’t measure
    // them. Summation is meant to start again from zero at the beginning of the next interval.

    @Serializable(with = KpiUnitSerializer::class)
    sealed interface KpiUnit {
        @Serializable data object Weight : KpiUnit

        @Serializable data object Length : KpiUnit

        @Serializable data object Time : KpiUnit

        @Serializable data object Currency : KpiUnit

        @Serializable data object Count : KpiUnit

        @Serializable data object Percentage : KpiUnit

        @Serializable data object Score : KpiUnit
    }

    @Serializable
    enum class KpiAccumulationType {
        Instantaneous, // one-time reading
        Summation, // total over the period
    }

    @Serializable(with = KpiOriginSerializer::class)
    sealed interface KpiOrigin {
        @Serializable
        data class Measured(
            val frequency: KpiFrequency,
            val unit: KpiUnit,
            val accumulationType: KpiAccumulationType,
        ) : KpiOrigin

        // A derived KPI is calculated based on other measurements or data sources.
        // Example 1: BMI = weight (kg) / (height (m))². You measure weight and height, then derive
        // BMI.
        // Example 2: “Average daily steps (this week)” = sum(daily steps) / 7.
        // Example 3: Debt-to-Income Ratio = (Total monthly debt payments) / (Monthly gross income).
        @Serializable data class Derived(val formula: KpiExpr) : KpiOrigin
    }

    // A user might define bizarre expressions or references that cause loops, e.g. KPI A references
    // KPI B which
    // references KPI A. Adding a new KPI must do:
    // * A topological check to ensure no circular dependencies.
    // * A check to ensure that all references are valid.
    // * A check to ensure that types are compatible (e.g. you can’t add a currency to a count).
    @Serializable
    sealed interface KpiExpr {
        @Serializable data class Literal(val value: Double) : KpiExpr

        @Serializable data class Add(val left: KpiExpr, val right: KpiExpr) : KpiExpr

        @Serializable data class Subtract(val left: KpiExpr, val right: KpiExpr) : KpiExpr

        @Serializable data class Multiply(val left: KpiExpr, val right: KpiExpr) : KpiExpr

        @Serializable data class Divide(val left: KpiExpr, val right: KpiExpr) : KpiExpr

        @Serializable data class Negate(val expr: KpiExpr) : KpiExpr

        @Serializable
        sealed interface EvaluationStrategy {
            @Serializable data object Fail : EvaluationStrategy

            // Default value to use if no data is available.
            @Serializable data class Default(val value: Double) : EvaluationStrategy

            // LOCF = Last Observation Carried Forward
            @Serializable
            data class LOCF(val maxDuration: TimeSpan, val fallback: EvaluationStrategy) :
                EvaluationStrategy

            // Extrapolate from the last known value.
            @Serializable
            data class Extrapolate(val duration: TimeSpan, val fallback: EvaluationStrategy) :
                EvaluationStrategy
        }

        @Serializable
        data class Reference(val kpiId: KpiId, val evaluationStrategy: EvaluationStrategy) : KpiExpr

        @Serializable
        sealed interface SeriesAggregationStrategy {
            @Serializable data class Sum(val duration: TimeSpan) : SeriesAggregationStrategy

            @Serializable
            data class SMA(val duration: TimeSpan, val granularity: TimeUnit?) :
                SeriesAggregationStrategy

            @Serializable
            data class EMA(val duration: TimeSpan, val granularity: TimeUnit?) :
                SeriesAggregationStrategy
        }

        @Serializable
        data class AggregateSeries(
            val expr: KpiExpr,
            val aggregationStrategy: SeriesAggregationStrategy,
        ) : KpiExpr

        @Serializable
        sealed interface SetAggregationStrategy {
            @Serializable data object Sum : SetAggregationStrategy

            @Serializable data object Average : SetAggregationStrategy

            @Serializable data object Min : SetAggregationStrategy

            @Serializable data object Max : SetAggregationStrategy

            @Serializable data object Count : SetAggregationStrategy
        }

        @Serializable
        data class TicketQuery(
            val query: TicketField,
            val filter: BoolExpr<TicketQ>,
            val aggregationStrategy: SetAggregationStrategy,
        ) : KpiExpr
    }

    @Serializable
    data class Kpi(
        val id: KpiId,
        val createdAt: Instant,
        val workspaceId: WorkspaceId,
        val projectId: ProjectId?,
        val name: String,
        val origin: KpiOrigin,
        // Plenty of personal KPIs are simply “trend watchers.”
        // For example, “Daily Mood” might not have a numeric target,
        // but you still want to see the average or the standard deviation.
        // In this case, the target value is optional.
        val targetValue: KpiTarget?,
    )

    @Serializable
    sealed interface KpiQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : KpiQ

        @Serializable data class WithName(val name: String) : KpiQ

        @Serializable data class WithProject(val projectId: ProjectId) : KpiQ

        @Serializable data object IsDerived : KpiQ

        @Serializable data object IsMeasured : KpiQ

        @Serializable data object IsTrendWatcher : KpiQ

        @Serializable data object HasTarget : KpiQ

        @Serializable data object IsTargetMet : KpiQ
    }

    @Serializable
    sealed interface KpiField {
        @Serializable data object TimeSinceCreation : KpiField

        @Serializable data object TimeUntilNextMeasurement : KpiField
    }

    @Serializable
    data class KpiMeasurement(
        val id: KpiMeasurementId,
        val kpiId: KpiId,
        val value: Double,
        val recordedAt: Instant,
    )

    @Serializable
    sealed interface KpiMeasurementQ {
        @Serializable data class WithKpi(val kpiId: KpiId) : KpiMeasurementQ

        @Serializable data class WithValue(val value: Double) : KpiMeasurementQ

        @Serializable data class RecordedAfter(val recordedAt: ZonedDateTime) : KpiMeasurementQ

        @Serializable data class RecordedBefore(val recordedAt: ZonedDateTime) : KpiMeasurementQ
    }

    @Serializable
    sealed interface KpiMeasurementField {
        @Serializable data object TimeSinceRecorded : KpiMeasurementField
    }

    @Serializable data class KpiWithTarget(val kpi: KpiId, val target: KpiTarget)

    // Objectives are meant to be SMART (Specific, Measurable, Achievable, Relevant, Time-bound).
    @Serializable
    data class Objective(
        val id: ObjectiveId,
        val createdAt: Instant,
        val workspaceId: WorkspaceId,
        val projectId: ProjectId?,
        // Specific: the intended outcome.
        val description: String,
        // Measurable: progress is derived from KPI measurements and closed tickets.
        val kpiTargets: List<KpiWithTarget>,
        // Execution rollup: linked work that advances the objective.
        val tickets: List<TicketId>,
        // Time-bound: the target date for the outcome.
        val deadline: ZonedDateTime?,
    )

    @Serializable
    sealed interface ObjectiveQ {
        @Serializable data class InWorkspace(val workspaceId: WorkspaceId) : ObjectiveQ

        @Serializable data class WithDescription(val description: String) : ObjectiveQ

        @Serializable data class WithProject(val projectId: ProjectId) : ObjectiveQ
    }

    @Serializable
    sealed interface ObjectiveField {
        @Serializable data object TimeSinceCreation : ObjectiveField

        @Serializable data object TimeUntilDeadline : ObjectiveField
    }

    data class RegisteredWorkspaceAccount(val userId: UserId, val workspaceId: WorkspaceId)

    data class CreatedAgentSubaccount(val userId: UserId, val token: String)

    // ────────────── Projects ──────────────
    suspend fun createWorkspace(name: String): WorkspaceId

    suspend fun getWorkspace(id: WorkspaceId): Workspace?

    suspend fun searchWorkspaces(query: BoolExpr<WorkspaceQ>): List<Workspace>

    suspend fun updateWorkspace(
        id: WorkspaceId,
        newName: Modification<String> = Modification.Keep,
    ): Workspace

    suspend fun addUserToWorkspace(
        workspaceId: WorkspaceId,
        userId: UserId,
        role: WorkspaceMembershipRole = WorkspaceMembershipRole.Member,
    ): WorkspaceMembership

    suspend fun updateWorkspaceMembershipRole(
        workspaceId: WorkspaceId,
        userId: UserId,
        role: WorkspaceMembershipRole,
    ): WorkspaceMembership

    suspend fun removeUserFromWorkspace(workspaceId: WorkspaceId, userId: UserId)

    suspend fun searchWorkspaceMemberships(
        query: BoolExpr<WorkspaceMembershipQ>
    ): List<WorkspaceMembership>

    suspend fun createUser(
        displayName: String,
        email: String? = null,
        role: UserRole = UserRole.User,
        kind: UserKind = UserKind.Human,
        ownerUserId: UserId? = null,
    ): UserId

    suspend fun deleteUser(id: UserId)

    suspend fun getUser(id: UserId): User?

    suspend fun searchUsers(query: BoolExpr<UserQ>): List<User>

    suspend fun setUserCredentials(id: UserId, username: String, password: String)

    suspend fun authenticateUser(username: String, password: String): User?

    suspend fun userForUsername(username: String): User?

    suspend fun userForEmail(email: String): User?

    suspend fun usernameForUser(userId: UserId): String?

    suspend fun registerWorkspaceAccount(
        workspaceName: String,
        displayName: String,
        username: String,
        password: String,
    ): RegisteredWorkspaceAccount

    suspend fun createAgentSubaccount(
        ownerUserId: UserId,
        displayName: String,
        username: String,
    ): CreatedAgentSubaccount

    suspend fun listOwnedAgentUsers(ownerUserId: UserId): List<User>

    suspend fun rotateAgentSubaccountToken(ownerUserId: UserId, agentUserId: UserId): String

    suspend fun userForAgentToken(token: String): User?

    suspend fun lastAuthenticatedAt(userId: UserId): Instant?

    suspend fun createSessionToken(userId: UserId): String

    suspend fun userForSessionToken(token: String): User?

    suspend fun revokeSessionToken(token: String): Boolean

    suspend fun updateUser(
        id: UserId,
        newDisplayName: Modification<String> = Modification.Keep,
        newEmail: Modification<String?> = Modification.Keep,
        newAvatarUrl: Modification<String?> = Modification.Keep,
        newTimeZoneId: Modification<String?> = Modification.Keep,
        newLocaleTag: Modification<String?> = Modification.Keep,
        newRole: Modification<UserRole> = Modification.Keep,
    ): User

    suspend fun createProject(
        workspaceId: WorkspaceId,
        name: String,
        parentProjectId: ProjectId? = null,
    ): ProjectId

    suspend fun deleteProject(id: ProjectId)

    suspend fun getProject(id: ProjectId): Project?

    suspend fun updateProject(
        id: ProjectId,
        newName: Modification<String> = Modification.Keep,
        newParentProjectId: Modification<ProjectId?> = Modification.Keep,
    ): Project

    suspend fun searchProjects(query: BoolExpr<ProjectQ>): List<Project>

    // ────────────── Tickets ──────────────
    suspend fun createTicket(
        projectId: ProjectId,
        title: String,
        deadline: ZonedDateTime?,
        priority: TicketPriority,
        urgency: TicketUrgency,
        actorUserId: UserId? = null,
        assigneeUserId: UserId? = null,
        stage: TicketStage = TicketStage.Todo,
        description: String = "",
    ): TicketId

    suspend fun deleteTicket(id: TicketId)

    suspend fun getTicket(id: TicketId): Ticket?

    suspend fun updateTicket(
        id: TicketId,
        newProjectId: Modification<ProjectId> = Modification.Keep,
        newTitle: Modification<String> = Modification.Keep,
        newDeadline: Modification<ZonedDateTime?> = Modification.Keep,
        newPriority: Modification<TicketPriority?> = Modification.Keep,
        newUrgency: Modification<TicketUrgency?> = Modification.Keep,
        newCompletedAt: Modification<Instant?> = Modification.Keep,
        newAssigneeUserId: Modification<UserId?> = Modification.Keep,
        newStage: Modification<TicketStage> = Modification.Keep,
        newDescription: Modification<String> = Modification.Keep,
        actorUserId: UserId? = null,
    ): Ticket

    suspend fun searchTickets(query: BoolExpr<TicketQ>): List<Ticket>

    suspend fun addTicketBlocker(
        blockedTicketId: TicketId,
        blockingTicketId: TicketId,
        actorUserId: UserId? = null,
    ): TicketBlocker

    suspend fun removeTicketBlocker(
        blockedTicketId: TicketId,
        blockingTicketId: TicketId,
        actorUserId: UserId? = null,
    )

    suspend fun searchTicketBlockers(query: BoolExpr<TicketBlockerQ>): List<TicketBlocker>

    suspend fun searchTicketActivities(query: BoolExpr<TicketActivityQ>): List<TicketActivity>

    // ────────────── Timeboxes ──────────────
    suspend fun createTimebox(
        workspaceId: WorkspaceId,
        projectId: ProjectId?,
        tickets: List<TicketId>,
        description: String,
        startTime: Instant,
        recordedDuration: TimeSpan?,
    ): TimeboxId

    suspend fun deleteTimebox(id: TimeboxId)

    suspend fun getTimebox(id: TimeboxId): Timebox?

    suspend fun updateTimebox(
        id: TimeboxId,
        newDescription: Modification<String> = Modification.Keep,
        newStartTime: Modification<Instant> = Modification.Keep,
        newRecordedDuration: Modification<TimeSpan?> = Modification.Keep,
    ): Timebox

    suspend fun searchTimeboxes(query: BoolExpr<TimeboxQ>): List<Timebox>

    // ────────────── Routines ──────────────
    suspend fun createRoutine(
        workspaceId: WorkspaceId,
        projectId: ProjectId?,
        description: String,
    ): RoutineId

    suspend fun deleteRoutine(id: RoutineId)

    suspend fun getRoutine(id: RoutineId): Routine?

    suspend fun updateRoutine(
        id: RoutineId,
        newDescription: Modification<String> = Modification.Keep,
    ): Routine

    suspend fun searchRoutines(query: BoolExpr<RoutineQ>): List<Routine>

    // ────────────── Events ──────────────
    suspend fun createEvent(
        workspaceId: WorkspaceId,
        projectId: ProjectId?,
        title: String,
        uid: String,
        dtStamp: ZonedDateTime,
        dtStart: ZonedDateTime,
        dtEnd: ZonedDateTime?,
        summary: String,
        description: String?,
        location: String?,
    ): EventId

    suspend fun deleteEvent(id: EventId)

    suspend fun getEvent(id: EventId): Event?

    suspend fun updateEvent(
        id: EventId,
        newTitle: Modification<String> = Modification.Keep,
        newUid: Modification<String> = Modification.Keep,
        newDtStamp: Modification<ZonedDateTime> = Modification.Keep,
        newDtStart: Modification<ZonedDateTime> = Modification.Keep,
        newDtEnd: Modification<ZonedDateTime?> = Modification.Keep,
        newSummary: Modification<String> = Modification.Keep,
        newDescription: Modification<String?> = Modification.Keep,
        newLocation: Modification<String?> = Modification.Keep,
    ): Event

    suspend fun searchEvents(query: BoolExpr<EventQ>): List<Event>

    // ────────────── KPIs ──────────────
    suspend fun createKpi(
        workspaceId: WorkspaceId,
        projectId: ProjectId?,
        name: String,
        origin: KpiOrigin,
        targetValue: KpiTarget?,
    ): KpiId

    suspend fun deleteKpi(id: KpiId)

    suspend fun getKpi(id: KpiId): Kpi?

    suspend fun updateKpi(
        id: KpiId,
        newName: Modification<String> = Modification.Keep,
        newOrigin: Modification<KpiOrigin> = Modification.Keep,
        newTargetValue: Modification<KpiTarget?> = Modification.Keep,
    ): Kpi

    suspend fun searchKpis(query: BoolExpr<KpiQ>): List<Kpi>

    // ────────────── KPI Measurements ──────────────
    suspend fun createKpiMeasurement(
        kpiId: KpiId,
        value: Double,
        recordedAt: Instant,
    ): KpiMeasurementId

    suspend fun deleteKpiMeasurement(id: KpiMeasurementId)

    suspend fun getKpiMeasurement(id: KpiMeasurementId): KpiMeasurement?

    suspend fun updateKpiMeasurement(
        id: KpiMeasurementId,
        newValue: Modification<Double> = Modification.Keep,
        newRecordedAt: Modification<Instant> = Modification.Keep,
    ): KpiMeasurement

    suspend fun searchKpiMeasurements(query: BoolExpr<KpiMeasurementQ>): List<KpiMeasurement>

    // ────────────── Objectives ──────────────
    suspend fun createObjective(
        workspaceId: WorkspaceId,
        projectId: ProjectId?,
        description: String,
        kpiTargets: List<KpiWithTarget>,
        tickets: List<TicketId>,
        deadline: ZonedDateTime?,
    ): ObjectiveId

    suspend fun deleteObjective(id: ObjectiveId)

    suspend fun getObjective(id: ObjectiveId): Objective?

    suspend fun updateObjective(
        id: ObjectiveId,
        newDescription: Modification<String> = Modification.Keep,
        newKpiTargets: Modification<List<KpiWithTarget>> = Modification.Keep,
        newTickets: Modification<List<TicketId>> = Modification.Keep,
        newDeadline: Modification<ZonedDateTime?> = Modification.Keep,
    ): Objective

    suspend fun searchObjectives(query: BoolExpr<ObjectiveQ>): List<Objective>

    companion object {
        val SpecialChars = CharSet.of(" -–_&,;.()[]?!@#$%^&*+=|")
        val AllowedUserChars = CharSet.letterOrDigit + SpecialChars
        val AllowedProjectChars = CharSet.letterOrDigit + SpecialChars
        val AllowedTicketChars = CharSet.letterOrDigit + SpecialChars
        val AllowedObjectiveChars = CharSet.letterOrDigit + SpecialChars
        val AllowedKpiChars = CharSet.letterOrDigit + SpecialChars
        val AllowedTimeboxChars = CharSet.letterOrDigit + SpecialChars
    }
}
